import React from 'react';
import {useParams} from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import Divider from "@mui/joy/Divider";
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Card from '@mui/joy/Card';
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import Avatar from "@mui/joy/Avatar";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HardwareIcon from '@mui/icons-material/Hardware';
import {NotificationService} from "../../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import { orderDetailsApi } from "../orderApi";
import { OrderPromoBadges } from "../OrderPromoBadges";
import { formatDateTime } from "../../../common/dateTime";
import { ForceActionType, GuaranteedEntity, OrderDetailsInfo, OrderFailReasonDescription, OrderStatus, OrderTypeDescription } from '../../../models/order';
import {formatRubles} from "../../../common/money";
import {OrderStatusIcon} from '../OrderStatusIcon';
import {OrderActions} from '../OrderActions';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Chip from '@mui/joy/Chip';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {PurchaseSource} from '../../../models/externalMarkets';
import {ExternalPurchaseCard} from './ExternalPurchaseCard';
import {OrderSkinsTransfer} from './OrderSkinsTransfer';

export function OrderDetails() {
    const {id} = useParams();
    const [orderInfo, setOrderInfo] = React.useState<OrderDetailsInfo | undefined>();
    const [loading, setLoading] = React.useState(false);
    const [forceModalOpen, setForceModalOpen] = React.useState(false);
    const [forceActionType, setForceActionType] = React.useState<ForceActionType>(ForceActionType.Handle);

    React.useEffect(() => {
        if (id && Number(id) > 0) {
            loadOrder(Number(id)).catch(console.error);
        }
    }, [id])

    const loadOrder = async (id: number) => {
        try {
            setLoading(true);
            let result = await orderDetailsApi(id);
            if (result.result) {
                setOrderInfo(result.value);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const defaultActionType = React.useMemo(
        () => orderInfo &&
              orderInfo.status === OrderStatus.ManualMode &&
              GuaranteedEntity.isGuaranteed(orderInfo)
                ? ForceActionType.Refund
                : ForceActionType.Complete,
        [orderInfo],
    );

    return (
        <React.Fragment>
            <OrderActions orderIds={[Number(id)]}
                        orderType={orderInfo?.type}
                        totalAmount={orderInfo?.total ?? 0}
                        actionType={forceActionType}
                        modalOpen={forceModalOpen}
                        setModalOpen={setForceModalOpen}
                        onCompleted={(success: boolean, actionType: ForceActionType, successIds: number[], failedIds: number[]) => {
                        if (forceActionType === ForceActionType.Handle) {
                            return;
                        }
                        if (success && successIds.length > 0) {
                            loadOrder(Number(id));
                        }}} />

            {loading
            ?<CircularProgress/>
            :<>
                <Card>
                    <Box sx={{mb: 1}}>
                        <Typography level="title-md">Информация</Typography>
                        <Typography level="body-sm">
                            Информация по заказу #<strong>{id}</strong> (<i>код - {orderInfo?.orderNumber}</i>)
                        </Typography>
                    </Box>
                    <Divider/>
                    <Stack spacing={1} sx={{flexGrow: 1}}>
                        <Typography level="body-xs">Тип: [{orderInfo ? OrderTypeDescription[orderInfo.type] : '-'}]</Typography>
                        {orderInfo?.source === PurchaseSource.ExternalMarket &&
                            <Chip size="sm" variant="soft" color="primary" startDecorator={<StorefrontIcon/>} sx={{alignSelf: 'flex-start'}}>
                                Поставщик: {orderInfo.externalMarketName || orderInfo.externalMarketKey}
                            </Chip>}
                        <OrderPromoBadges promoCodes={orderInfo?.promoCodes}/>
                        <Typography level="body-xs">Создан: {formatDateTime(orderInfo?.createdAt)}</Typography>
                        {orderInfo?.requestedFromSellerAt &&
                            <Typography level="body-xs">Запрос к продавцу: {formatDateTime(orderInfo?.requestedFromSellerAt)}</Typography>}
                        {orderInfo?.receivedItemsAt &&
                            <Typography level="body-xs">Предметы получены: {formatDateTime(orderInfo?.receivedItemsAt)}</Typography>}
                        {orderInfo?.sentToBuyerAt &&
                            <Typography level="body-xs">Запрос к покупателю: {formatDateTime(orderInfo?.sentToBuyerAt)}</Typography>}
                        {orderInfo?.completedAt
                        ? <Typography level="body-xs">Завершен: {formatDateTime(orderInfo?.completedAt)}</Typography>
                        :<></>}
                        <Stack spacing={1}
                            direction={'row'}
                            sx={{
                                justifyContent: "left",
                                alignItems: "center",
                        }}>
                            <OrderStatusIcon status={orderInfo?.status}/>
                            {!orderInfo || OrderStatus.isCompleted(orderInfo?.status)
                            ?<></>
                            :<IconButton
                                size="sm"
                                variant="outlined"
                                color="warning"
                                aria-label="Снять пометку бана"
                                onClick={() => {
                                    setForceActionType(ForceActionType.Handle);
                                    setForceModalOpen(true);
                                }}
                            >
                                <RocketLaunchIcon/>
                            </IconButton>}
                            {orderInfo &&
                                <OrderSkinsTransfer order={orderInfo} onCompleted={() => loadOrder(Number(id))}/>}
                            {orderInfo?.status === OrderStatus.ManualMode
                                ?<IconButton
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => {
                                        setForceActionType(defaultActionType);
                                        setForceModalOpen(true);
                                    }}
                                >
                                    <HardwareIcon/>
                                </IconButton>
                                :<></>}
                        </Stack>
                        {orderInfo?.failReason
                        ?<Typography level="body-sm" fontWeight="700" color='danger'>Ошибка: {OrderFailReasonDescription[orderInfo?.failReason]}</Typography>
                        :<></>}
                        {orderInfo?.comment
                        ?<Typography level="body-xs">Комментарий: {orderInfo?.comment}</Typography>
                        :<></>}
                        <Typography level="body-xs">Игра: {orderInfo?.gameName}</Typography>
                        <Stack spacing={1} direction={'row'} sx={{justifyContent: "flex-start", alignItems: "flex-start"}}>
                            <Typography level="body-xs">Продавец:</Typography>
                            <Link
                                underline="hover"
                                href={`/app/users/${orderInfo?.sellerId.toString()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                level="body-xs"
                            >
                                {orderInfo?.sellerId.toString()}
                            </Link>
                            <Link
                                underline="hover"
                                href={`https://steamcommunity.com/profiles/${orderInfo?.sellerId.toString()}/`}
                                target="_blank"
                                level="body-xs"
                            >
                                (Steam link)
                            </Link>
                        </Stack>
                        <Stack spacing={1} direction={'row'} sx={{justifyContent: "flex-start", alignItems: "flex-start"}}>
                            <Typography level="body-xs">Покупатель:</Typography>
                            <Link
                                underline="hover"
                                href={`/app/users/${orderInfo?.buyerId.toString()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                level="body-xs"
                            >
                                {orderInfo?.buyerId.toString()}
                            </Link>
                            <Link
                                underline="hover"
                                href={`https://steamcommunity.com/profiles/${orderInfo?.buyerId.toString()}/`}
                                target="_blank"
                                level="body-xs"
                            >
                                (Steam link)
                            </Link>
                        </Stack>
                        {orderInfo?.botId ?
                            <Stack spacing={1} direction={'row'} sx={{justifyContent: "flex-start", alignItems: "flex-start"}}>
                                <Typography level="body-xs">Бот:</Typography>
                                <Link
                                    underline="hover"
                                    href={`/app/bots/${orderInfo.botId?.toString()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    level="body-xs"
                                >
                                    #{orderInfo.botId.toString()}
                                </Link>
                                <Link
                                    underline="hover"
                                    href={`https://steamcommunity.com/profiles/${orderInfo.botSteamId?.toString()}/`}
                                    target="_blank"
                                    level="body-xs"
                                >
                                    (Steam)
                                </Link>
                            </Stack>
                            : <></>
                        }
                        {orderInfo?.bidId
                        ?<Stack spacing={1} direction={'row'} sx={{justifyContent: "flex-start", alignItems: "flex-start"}}>
                            <Typography level="body-xs">Заявка:</Typography>
                            <Link
                                underline="hover"
                                href={`/app/bids?bidId=${orderInfo.bidId}#list`}
                                target="_blank"
                                rel="noopener noreferrer"
                                level="body-xs"
                            >
                                #{orderInfo.bidId}
                            </Link>
                            {orderInfo.byOfflineBid &&
                                <Chip size="sm" variant="soft" color="neutral"
                                      title="Покупатель был не в сети, офлайн-заявки включены глобально и в настройках покупателя">
                                    офлайн-заявка
                                </Chip>}
                        </Stack>
                        :<></>}
                        {orderInfo?.groupId
                        ? <Typography level="body-xs">Группа: {orderInfo.groupId?.toString()}</Typography>
                        : <></>
                        }
                        <Typography level="body-xs">
                            Сумма: {formatRubles(orderInfo?.total)} ({orderInfo?.source === PurchaseSource.ExternalMarket ? 'плановая прибыль' : 'комиссия'} {formatRubles(orderInfo?.feeAmount)})
                        </Typography>
                        {orderInfo?.source === PurchaseSource.ExternalMarket &&
                            <ExternalPurchaseCard purchase={orderInfo.externalPurchase}
                                                  marketName={orderInfo.externalMarketName || orderInfo.externalMarketKey}
                                                  providerKey={orderInfo.externalMarketKey}
                                                  onChanged={() => loadOrder(Number(id)).catch(console.error)}/>}
                        {orderInfo?.status === OrderStatus.ManualMode && orderInfo?.receivedItemsAt ?
                            <Stack spacing={1}
                                direction={'row'}
                                sx={{
                                        justifyContent: "left",
                                        alignItems: "center"
                                    }}
                            >
                                {orderInfo.guaranteedUpTo&&
                                <>
                                    <Typography level="body-xs">Гарантия хранения:</Typography>
                                    {new Date(orderInfo.guaranteedUpTo!) < new Date()
                                        ? <CancelIcon fontSize='medium' color='error' />
                                        : <CheckCircleIcon fontSize='medium' color='success' />}
                                </>
                                }

                                <Stack spacing={0} direction={'column'}>
                                    <Typography level="body-xs">{formatDateTime(orderInfo.receivedItemsAt!)}</Typography>
                                    <Typography level="body-xs" marginTop={'-10px'}>..</Typography>
                                    {orderInfo.guaranteedUpTo&&<Typography level="body-xs">{formatDateTime(orderInfo.guaranteedUpTo!)}</Typography>}
                                </Stack>
                            </Stack>
                            :<></>
                        }
                        <br />
                        <Typography level="title-md">Состав заказа:</Typography>
                        <Sheet
                            className="OrderTableContainer"
                            sx={{
                                display: {xs: 'none', sm: 'initial'},
                                width: '100%',
                                borderRadius: 'sm',
                                flexShrink: 1,
                                overflow: 'auto',
                                minHeight: 0,
                            }}
                        >
                            <Table
                                aria-labelledby="tableTitle"
                                stickyHeader
                                hoverRow
                                sx={{
                                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                    '--Table-headerUnderlineThickness': '1px',
                                    '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                    '--TableCell-paddingY': '4px',
                                    '--TableCell-paddingX': '8px',
                                }}
                            >
                                <thead>
                                <tr>
                                    <th style={{width: 70, padding: '12px 6px'}}>#</th>
                                    <th style={{width: 70, padding: '12px 6px'}}>Steam Id</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Скин</th>
                                    <th style={{width: 100, padding: '12px 6px'}}>Цена</th>
                                </tr>
                                </thead>
                                <tbody>
                                {orderInfo?.items.map((row) => (
                                    <tr key={row.id}>
                                        <td><Typography level="body-xs">{row.id}</Typography></td>
                                        <td><Typography level="body-xs">{row.assetId.toString()}</Typography></td>
                                        <td>
                                            <Tooltip title={row.skinName}>
                                                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                                    <Avatar
                                                        variant="outlined"
                                                        size="sm"
                                                        src={row.image}
                                                    />
                                                    <Link
                                                        underline="hover"
                                                        href={`https://steamcommunity.com/market/search?q=${row.skinName}`}
                                                        target="_blank"
                                                        level="body-xs"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: '2',
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {row.skinName}
                                                    </Link>
                                                </Stack>
                                            </Tooltip>
                                        </td>
                                        <td><Typography level="body-xs">{formatRubles(row.price)} (комиссия - {formatRubles(row.commission)})</Typography></td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Sheet>
                        <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                            {orderInfo?.items.map((listItem) => (
                                <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                    <ListItem
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                            <div>
                                                <Typography gutterBottom sx={{fontWeight: 600}}>#{listItem.id}</Typography>
                                                <Stack spacing={1} direction={'row'}>
                                                    <Link
                                                        underline="hover"
                                                        href={`https://steamcommunity.com/market/search?q=${listItem.skinName}`}
                                                        target="_blank"
                                                        level="body-xs"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: '2',
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {listItem.skinName}
                                                    </Link>
                                                    <Typography level="body-xs">({listItem.assetId.toString()})</Typography>
                                                </Stack>
                                                <Typography level="body-xs">{formatRubles(listItem.price)} (комиссия - {formatRubles(listItem.commission)})</Typography>
                                            </div>
                                        </ListItemContent>
                                        {listItem.image
                                            ? <Box component="img" src={listItem.image} sx={{ height: "30px", width: "auto" }} />
                                            : <></>}
                                    </ListItem>
                                    <ListDivider/>
                                </List>
                            ))}
                        </Box>
                    </Stack>
                </Card>
            </>}
        </React.Fragment>
    );
}
